import React, { useEffect, useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { isEmpty, cloneDeep } from 'lodash';

import { Grid, getSelectedState, GridColumn as Column } from '@progress/kendo-react-grid';
import { getter } from '@progress/kendo-react-common';
import { orderBy, filterBy } from '@progress/kendo-data-query';

import CircularProgress from '@material-ui/core/CircularProgress';

import DevicesTableRowMenu from './DevicesTableRowMenu';

import * as DeviceActions from '../../actions/deviceActions';
import { kendoGridInlineStyling } from '../../helpers/kendoGridInlineStyleHelper';
import classes from './NiagaraDevicesTable.module.css';

const idGetter = getter('id');

const DetailComponent = props => {
    const [filteredObjects, setFilteredObjects] = useState([]);
    const [dataState, setDataState] = useState({ sort: [], take: 20, skip: 0 });
    const [gridData, setGridData] = useState([]);

    useEffect(() => {
        if (props.dataItem.expandedObjects && isEmpty(gridData)) initializeTable();
    }, [props.dataItem]);

    const initializeTable = () => {
        const data = [...props.dataItem.expandedObjects];
        const initialData = data.splice(0, 20);

        setFilteredObjects(data);
        setGridData(initialData);
    };

    const renderProgress = () => {
        return (
            <CircularProgress
                size={25}
            />
        );
    };

    if (!props.dataItem.expandedObjects) {
        return (
            <div
                style={{
                    height: '50px',
                    width:  '100%',
                }}
            >
                <div
                    style={{
                        position:   'absolute',
                        left:       '50%',
                        paddingTop: '1rem'
                    }}
                >
                    {renderProgress()}
                </div>
            </div>
        );
    }

    const sampleIntervalCell = props => {
        return (
            <td>
                {props.dataItem.changeOfValue ? 'CoV' : props.dataItem.sampleInterval}
            </td>
        );
    };


    const scrollHandler = (event) => {
        const e = event.nativeEvent;

        if (
            e.target.scrollTop + 10 >=
            e.target.scrollHeight - e.target.clientHeight
        ) {
            const moreData = filteredObjects.splice(0, 20);

            if (moreData.length > 0) {
                let newData = [...gridData, ...moreData];
                setGridData(newData);
            }
        }
    };

    const dataStateChange = e => {
        const parent = document.getElementById('subTable');

        if (parent) {
            let children = parent.getElementsByClassName('k-grid-content k-virtual-content');
            children[0].scrollTop = 0;
        }

        const objs = [...props.dataItem.expandedObjects];
        const data = orderBy(filterBy(objs, e.dataState.filter), e.dataState.sort);
        const filteredData = data.splice(0, 20);

        setFilteredObjects(data);
        setDataState(e.dataState);
        setGridData(filteredData);
    };

    return (
        <div className={classes.subTable} id="subTable">
            <Grid
                sortable
                onScroll={scrollHandler}
                {...dataState}
                onDataStateChange={dataStateChange}
                data={gridData ? gridData : []}
                fixedScroll={true}
                filterable={props.dataItem.workbookMode ? true : false}
            >
                <Column field="name" title="Name" width="250px" />
                <Column field="objectType" title="Type" width="150px" />
                <Column field="dataType" title="Data Type" width="150px" />
                <Column field="oBIXPath" title="Obix Path" />
                <Column
                    field="sampleInterval"
                    title="Trend Interval"
                    cell={sampleIntervalCell}
                    width="150px"
                />
            </Grid>
        </div>
    );
};


const initialDataState = {
    filter: [],
    sort:   [], 
    take:   20, 
    skip:   0    
};

const NiagaraDevicesTable = ({
    agentId,
    devices,
    deviceObjectsLoading,
    fetchDeviceObjects,
    companyInfo,
    isFullHubAllowed,
    onDeleteModal,
    onImportModal,
    onManageModal,
    onDeviceToggle,
    reloadDeviceObjects,
    setDevice,
    updateSelectedState,
    workbookModeEnabled,
    ...props
}) => {
    const [objects, setObjects] = useState([]);
    const [filteredObjects, setFilteredObjects] = useState([]);
    const [dataState, setDataState] = useState(initialDataState);
    const [gridData, setGridData] = useState([]);
    const [tableSelectState, setTableSelectState] = useState({});

    useEffect(() => {
        if (devices && isEmpty(gridData)) initializeTable();
    }, [devices]);

    useEffect(() => {
        const updatedObjects = [...devices];

        if (!workbookModeEnabled) {
            setDataState(initialDataState);
            updatedObjects.forEach(item => item.workbookMode = false);
            setObjects(updatedObjects);
            initializeTable();
        } else {
            updatedObjects.forEach(item => item.workbookMode = true);
            setObjects(updatedObjects);
        }

    }, [workbookModeEnabled]);

    const initializeTable = () => {
        setObjects(devices);

        const data = [...devices];
        const initialData = data.splice(0, 20);

        setFilteredObjects(data);
        setGridData(initialData);
    };

    const scrollHandler = (event) => {
        const e = event.nativeEvent;

        if (
            e.target.scrollTop + 10 >=
            e.target.scrollHeight - e.target.clientHeight
        ) {
            const moreData = filteredObjects.splice(0, 20);

            if (moreData.length > 0) {
                let newData = [...gridData, ...moreData];
                setGridData(newData);
            }
        }
    };

    const dataStateChange = e => {
        const parent = document.getElementById('table');

        if (parent) {
            let children = parent.getElementsByClassName('k-grid-content k-virtual-content');
            children[0].scrollTop = 0;
        }

        const objs = [...objects];
        const data = orderBy(filterBy(objs, e.dataState.filter), e.dataState.sort);
        const filteredData = data.splice(0, 20);

        setFilteredObjects(data);
        setDataState(e.dataState);
        setGridData(filteredData);
    };

    const onHeaderSelectionChange = (event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(tableSelectState);

        const filteredData = orderBy(filterBy(gridData, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });

        updateSelectedState(newSelectedState);
        setTableSelectState(newSelectedState);
    };

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: tableSelectState,
            dataItemKey:   'id',
        });

        updateSelectedState(newSelectedState);
        setTableSelectState(newSelectedState);
    };

    const expandChange = async (event) => {
        let newData = [];

        newData = objects.map((item) => {
            if (item.id === event.dataItem.id) {
                item.expanded = !event.dataItem.expanded;
            }

            return item;
        });

        setObjects(newData);

        if (!event.dataItem.expanded && !event.dataItem.expandedObjects) {
            const deviceObjects = await fetchDeviceObjects(event.dataItem.id);

            newData = objects.map((item) => {
                if (item.id === event.dataItem.id) {
                    item.expandedObjects = [...deviceObjects];
                }

                return item;
            });
        }

        setObjects(newData);
    };

    const renderMenu = rowData => {
        return (
            <td>
                <DevicesTableRowMenu
                    agentId={agentId}
                    device={rowData}
                    companyInfo={companyInfo}
                    hidden={!isFullHubAllowed}
                    onDeleteModal={() => onDeleteModal(rowData)}
                    onImportModal={() => onImportModal(rowData)}
                    onManageModal={() => onManageModal(rowData)}
                    profileId={rowData.basProfileId}
                    setDevice={setDevice}
                    reloadDeviceObjects={reloadDeviceObjects}
                />
            </td>
        );
    };

    return (
        <div className={classes.NiagaraDevicesTable} id="table">
            <style>
                { kendoGridInlineStyling }
                {
                    `.k-grid .k-hierarchy-cell .k-i-plus:before{
                        content: '\\e005';
                    }
                    .k-grid .k-hierarchy-cell .k-i-minus:before{
                        content: '\\e006';
                    }
                    `
                }
            </style>
            <Grid
                style={{
                    height: '54vh',
                    border: 'none'
                }}
                sortable
                dataItemKey="id"
                selectedField="selected"
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                data={gridData.map((item) => ({
                    ...item, 'selected': tableSelectState[idGetter(item)],
                }))}
                onScroll={scrollHandler}
                {...dataState}
                detail={DetailComponent}
                expandField="expanded"
                onExpandChange={expandChange}
                onDataStateChange={dataStateChange}
                fixedScroll={true}
                filterable={workbookModeEnabled}
            >
                <Column
                    filterable={false}
                    field="selected"
                    width="75px"
                    headerSelectionValue={
                        orderBy(filterBy(gridData, dataState.filter), dataState.sort).length > 0 && 
                        orderBy(filterBy(gridData, dataState.filter), dataState.sort).findIndex((item) => !tableSelectState[idGetter(item)]) === -1
                    }
                />
                <Column
                    field="name"
                    title="Name"
                    width="auto"
                />
                <Column
                    field="basProfileName"
                    title="Profile"
                    width={280}
                />
                <Column
                    field="type"
                    title="Type"
                    width={220}
                />
                <Column
                    field="oBIXPath"
                    title="Obix Path"
                    width={400}
                />
                <Column
                    field=""
                    title=""
                    cell={renderMenu}
                    locked={true}
                    sortable={false}
                    filterable={false}
                    width={75}
                />
            </Grid>
        </div>
    );
};

NiagaraDevicesTable.defaultProps = {
    deviceObjectsLoading: false,
    isFullHubAllowed:     true,
    onDeviceToggle:       () => {}
};

NiagaraDevicesTable.propTypes = {
    agentId:              string,
    devices:              array.isRequired,
    deviceObjectsLoading: bool,
    companyInfo:          object,
    isFullHubAllowed:     bool,
    onDeviceToggle:       func
};

const mapDispatchToProps = {
    ...DeviceActions
};

export default connect(null, mapDispatchToProps)(NiagaraDevicesTable);
